import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { CrossfadePass } from './crossfade-pass.js';
import { LyricFlashLayer } from './lyric-flash.js';
import { updateSafeZoneUniforms } from './shaders/safeZone.glsl.js';
import { computeBeatGrid, PositionSmoother } from './beat-grid.js';
import { lerpPalette, PALETTE_BLEND_MS, PALETTE_BLEND_STEPS } from './palette-blend.js';
import { computeDirector } from './director.js';
import { FREEZE_TIME_S, FREEZE_STEP_S, advanceFrozenClock } from './debug-capture.js';

// Chromatic aberration + vignette + film grain in one combined pass
// (cheaper than three separate full-screen passes) - what actually reads
// as "produced/filmic" rather than a clean, obviously-computed render.
// Grain is re-randomized every frame (via uTime) rather than a static
// overlay texture, so it reads as grain and not a baked-in pattern.
// Per-visualizer post-processing overrides.
//
// Every visualizer shares one composer, and the look that chain imposes -
// chromatic aberration, a 45% vignette, grain, bloom, an ACES curve - is part
// of the house style and right for almost everything in the library. It is
// wrong for exactly one kind of content: a PHOTOGRAPH. Aberration and a heavy
// vignette are lens defects being simulated on top of an image that already
// went through a real lens, and an ACES curve is a second grade on top of the
// one the photographer already applied. gallery.js is the first visualizer
// that needs the chain to step back, and it says so by exposing a
// `postProcessing` object naming any of the keys below.
//
// Anything a visualizer does not name keeps the library default, and a
// visualizer that exposes nothing at all (every other one) is unaffected -
// _applyPostProfile restores this table in full on every swap, so an override
// can never leak into the next visualizer.
const POST_DEFAULTS = {
  aberration: 0.0022,
  vignette: 0.45,
  grain: 0.02,
  bloomStrength: 0.7,
  bloomThreshold: 0.78,
  toneMapping: THREE.ACESFilmicToneMapping,
  toneMappingExposure: 1.1
};

const FILMIC_SHADER = {
  uniforms: {
    tDiffuse: { value: null },
    uTime: { value: 0 },
    uAberration: { value: 0.0022 },
    uVignette: { value: 0.45 },
    // Was 0.035, uniform everywhere - reads as fine on the mostly-dark/
    // cluttered particle areas (existing detail masks it) but stood out
    // as visible speckle on the rig's beams specifically, since those are
    // large smooth bloom-brightened gradients with nothing else to hide
    // the noise in. Lowered, and now also scaled down further on bright
    // pixels in the shader below (see luminance-based falloff) rather
    // than applied at a flat strength regardless of what's underneath.
    uGrain: { value: 0.02 },
    // 0..1, one frame wide - see director.js's own flash field. Consumed
    // here rather than a separate pass (the brief's own "zero passe
    // supplementaire" requirement for the FLASH transition type -
    // chantier 6/adaptive-party.js).
    uFlash: { value: 0 },
    // Le réglage de luminosité de la console (config-utilisateur.js,
    // facteurLuminosite) : un gain sur l'animation seule, 1 = d'origine.
    uLuminosite: { value: 1 }
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float uTime;
    uniform float uAberration;
    uniform float uVignette;
    uniform float uGrain;
    uniform float uFlash;
    uniform float uLuminosite;
    varying vec2 vUv;

    float hash12(vec2 p) {
      vec3 p3 = fract(vec3(p.xyx) * 0.1031);
      p3 += dot(p3, p3.yzx + 33.33);
      return fract((p3.x + p3.y) * p3.z);
    }

    void main() {
      vec2 centered = vUv - 0.5;
      float dist = length(centered);

      // Stronger toward the edges, like real lens dispersion - not a
      // uniform offset across the whole frame.
      vec2 dir = centered * dist * uAberration;
      float r = texture2D(tDiffuse, vUv - dir).r;
      float g = texture2D(tDiffuse, vUv).g;
      float b = texture2D(tDiffuse, vUv + dir).b;
      vec3 color = vec3(r, g, b);

      float vig = smoothstep(0.9, 0.25, dist);
      color *= mix(1.0 - uVignette, 1.0, vig);

      // GRAIN MULTIPLICATIF, and this pass runs in LINEAR light: it sits
      // between the bloom and OutputPass, so ACES and the sRGB encode are
      // still ahead of it. Near black that curve has a slope around 13, so
      // the old ADDITIVE grain - plus or minus 0.01 linear, and deliberately
      // strongest where it was darkest - came out as plus or minus 25/255 on
      // screen. On a night scene it was not grain, it was a boil: measured
      // frame to frame on fireflies, the mean absolute change was 6.5 to 7.8
      // out of 255 over a picture whose own mean was 4 to 17. It also lifted
      // the blacks, because the positive half of the swing brightens far more
      // than the negative half darkens (sky 5.3 -> 6.8, meadow 2.1 -> 4.3).
      // Multiplying instead makes the perturbation proportional to what is
      // already there: black stays black, and a lit surface keeps the same
      // relative texture it always had. That also makes the old brightness
      // taper redundant - it existed to patch the additive form's wrongness.
      // Le facteur n'est pas un choix de goût : uGrain était calibré pour une
      // amplitude ABSOLUE, et la même amplitude devenue RELATIVE ne veut plus
      // rien dire. Balayé sur une zone claire ET lisse - le disque de la lune
      // de fireflies - le grain spatial y vaut 0,48 sans grain du tout, 0,69
      // à x1,5, 0,95 à x2,5, 1,53 à x4, 2,45 à x7 ; l'ancienne version
      // additive rendait 2,51. Reproduire exactement l'ancien niveau (x7)
      // restait trop visible sur une scène de nuit : le scintillement image à
      // image y valait encore 2,80 sur la lune et 0,41 sur le ciel sombre.
      // x2,5 les ramène à 1,03 et 0,15, en gardant un grain deux fois
      // au-dessus du détail propre de la scène.
      float grain = (hash12(vUv * vec2(1920.0, 1080.0) + uTime) - 0.5) * uGrain * 2.5;
      color *= 1.0 + grain;

      // A hard cut's whole-frame flash - additive toward white, not a
      // replace, so it reads as a bright pop rather than a literal blank.
      color += uFlash;

      // LA LUMINOSITÉ réglée dans la console, après le flash pour qu'une coupe
      // reste proportionnée à la scène. En linéaire, avant ACES : un gain
      // d'exposition, pas un voile.
      color *= uLuminosite;

      gl_FragColor = vec4(color, 1.0);
    }
  `
};

// Slow, permanent camera drift ("Ken Burns") applied on top of whatever
// static position/lookAt each visualizer sets in init() - a perfectly
// still camera is a big part of what reads as a screensaver rather than
// produced content. Small enough that no visualizer's framing breaks.
const DRIFT_POSITION_AMOUNT = 0.22;
const DRIFT_ROTATION_AMOUNT = 0.012; // radians

// Published by the SceneManager constructor once a GL context exists (see
// ALIASED_POINT_SIZE_RANGE there). 64 is a safe floor until then.
let maxPointSize = 64;
export function getMaxPointSize() {
  return maxPointSize;
}

// Published the same way and for the same reason as maxPointSize above: only
// the object that owns a GL context can read it, and a visualizer that wants
// it has nowhere else to ask. 1 is "no anisotropic filtering", which is the
// correct no-op default until a context exists.
let maxAnisotropy = 1;
export function getMaxAnisotropy() {
  return maxAnisotropy;
}

// Published from _applyQualityTier for the same reason as the two above:
// the EFFECTIVE drawing-buffer height (CSS height x BASE_RENDER_SCALE x
// pixel ratio x tier scale) is what bloom actually operates on, and a
// visualizer guarding against small-buffer bloom spread (clave.js) has
// nowhere else to read it - window.innerHeight * devicePixelRatio is
// wrong on both axes at once (the renderer caps dpr at 1 and scales by
// tier). 1080 is a safe "large screen, no guard" default until a real
// size has been applied.
let renderBufferHeight = 1080;
export function getRenderBufferHeight() {
  return renderBufferHeight;
}

const _driftEuler = new THREE.Euler();
const _driftQuat = new THREE.Quaternion();

// Renders the *sharp* content (base scene, filmic pass) at this fraction
// of the canvas's CSS size - kept close to 1 because this is the layer
// where reducing resolution is actually visible (particle edges, beam
// silhouettes, text-adjacent detail all live here). A first pass used
// 0.78 for everything including this layer and reportedly looked
// pixelated on the real TV - that was too aggressive for content that's
// supposed to read as sharp. See BLOOM_SCALE below for where the real
// perf budget should come from instead.
const BASE_RENDER_SCALE = 0.94;

// Bloom's own internal resolution, as a fraction of BASE_RENDER_SCALE's
// (already near-full) resolution - independent of and much more
// aggressive than BASE_RENDER_SCALE. Bloom is inherently a blur, so a
// coarser internal resolution for it specifically is invisible, while
// its multi-pass blur chain is the single most expensive thing in this
// whole pipeline - this is where the actual perf budget should be spent,
// not on softening the sharp base image.
const BLOOM_SCALE = 0.42;

// CHANTIER 7 - performance governor. A rolling average of the last 90
// frames' own dt (not a single-frame sample, which is noisy enough to
// false-trigger on one hitch) decides whether to step the quality tier
// down/up or lock to a fixed 30fps timestep. Both directions require the
// average to have been past its threshold continuously for a while (2s
// down, 8s up) - a brief blip shouldn't cost a tier change either way.
const FRAME_HISTORY_SIZE = 90;
// Raised from 26 to sit above LOCK_30FPS_MAX_MS (30): at the old value, a
// steadily locked 30fps (a perfectly tolerable, intentionally-supported
// steady state - see LOCK_30FPS_MIN/MAX_MS below) sat ABOVE this threshold
// too, so a TV happily holding a stable 30fps could still get tier-- out
// from under it after 2s, silently stripping enrichments (e.g. laser-
// cathedral's floor/reflection) that were never the actual bottleneck.
// Now only frame times genuinely worse than a locked 30fps trigger a drop.
const HIGH_FRAME_MS = 33;
const HIGH_FRAME_SUSTAINED_S = 2;
// Tier-up used to be an ABSOLUTE frame time: avgMs < 15, i.e. sustained 66fps
// or better. requestAnimationFrame is vsync-locked, so on a 60Hz panel the
// best achievable average is 16.7ms and on a 50Hz one 20ms - neither can ever
// get under 15. The governor could therefore go DOWN but never back UP on any
// ordinary display: one momentary dip on a heavy visualizer pinned the device
// at tier 0 for the rest of the session, rendering everything at a 0.7 pixel
// ratio for no reason. Measured on the cheapest visualizer in the library,
// running perfectly at vsync: avgMs 17.0, tier still 0 after 50s, long past
// the cooldown. That is what put the television at tier 0 permanently and made
// TypeSlam's type look upscaled.
//
// The question the upgrade test actually wants to ask is "are we vsync-bound
// rather than GPU-bound", which is relative to what the display can deliver.
// So it compares against a running estimate of the refresh period instead
// (see _refreshMs), with a little slack for jitter.
const UPGRADE_SLACK = 1.08;
// The estimate is clamped to periods a real panel can have. The floor covers
// high-refresh displays; the ceiling is the important one - without it a
// device that is permanently slow would take its own bad cadence as the
// target and cheerfully upgrade into the ground. 21ms still admits a 50Hz TV
// (20ms); anything slower than that is the device struggling, not the panel.
const REFRESH_MS_MIN = 6;
const REFRESH_MS_MAX = 21;
const LOW_FRAME_SUSTAINED_S = 8;
// Tier-up is additionally rate-limited on its own cooldown - a tier that
// just stepped down shouldn't immediately step back up on the first good
// window afterward and start oscillating.
const TIER_UP_COOLDOWN_S = 30;
// A steady 30fps reads far better than an oscillating 40-50fps - the brief's
// own reasoning, and the primary cause of TV-visible stutter in practice.
const LOCK_30FPS_MIN_MS = 17;
const LOCK_30FPS_MAX_MS = 30;
// Trois quarts de côté, soit 44 % de pixels en moins. Sur des visuels flous
// et lumineux, la perte se voit à peine ; le gain, lui, est proportionnel au
// nombre de pixels que le fragment shader doit remplir.
const POWER_SAVE_SCALE = 0.75;

// Le plus grand pas de temps qu'une image ait le droit de représenter. Au-delà
// on n'a pas affaire à une image lente mais à une interruption.
const MAX_FRAME_DT_S = 0.1;

const FIXED_TIMESTEP_30FPS_S = 1 / 30;
const MIN_QUALITY_TIER = 0;
const MAX_QUALITY_TIER = 2;
// Index = tier. Multiplies BOTH the base pixel-ratio cap and BLOOM_SCALE -
// bloom is already the most expensive pass (see BLOOM_SCALE's own
// comment), so it benefits from the same tier cut as the main render.
const TIER_RESOLUTION_SCALE = [0.7, 0.85, 1.0];

// Le champ de vision de référence, celui pour lequel toutes les scènes sont
// composées. Nommé plutôt que posé en dur dans le constructeur, parce qu'il
// faut désormais pouvoir y REVENIR : voir setVisualizer.
const BASE_FOV = 68;

// LA CAMÉRA DE RÉFÉRENCE, remise avant CHAQUE visuel (01/10/2026). Champ de
// vision, zoom, plans de coupe, décalage d'image, position et visée : un
// visualiseur qui en change un et ne le rend pas le laissait aux suivants.
// Beamwall pose 34° (le défaut est 68°) : après lui, Bayfront et Mainstage
// s'affichaient zoomés d'un facteur deux, d'un visuel à l'autre, jusqu'au
// prochain qui posait son propre champ. Et la transition PUSH avance la
// caméra : le visuel suivant en héritait, les poussées s'additionnaient.
// L'aspect n'est pas touché : c'est le redimensionnement qui le tient.
export function reinitialiserCamera(camera) {
  if (!camera) return;
  camera.fov = BASE_FOV;
  camera.zoom = 1;
  camera.near = 0.1;
  camera.far = 100;
  camera.filmOffset = 0;
  if (camera.view) camera.clearViewOffset();
  camera.up.set(0, 1, 0);
  camera.position.set(0, 1.4, 6);
  camera.lookAt(0, 1, 0);
  camera.updateProjectionMatrix();
}

// The half-size, in CSS px, the cover block WOULD have centred - for the
// stage (R19) while the cover sits in a corner. play.html's #overlay is a
// column when centred (cover, then the text 1.4rem below it, the text block
// at most 60vw wide, each line on one line) and a row in a corner; the two
// layouts share the cover's size and the text's height. Any other element
// (the capture harness's plain box, the landing page's #track) keeps its own
// size.
function centredHalfEstimate(el, rect, vw) {
  const art = el && el.querySelector ? el.querySelector('#overlay-artwork') : null;
  if (!art) return { w: rect.width / 2, h: rect.height / 2 };
  const a = art.getBoundingClientRect();
  let cw = a.width, ch = a.height;
  const txt = el.querySelector('#overlay-text');
  const t = txt ? txt.getBoundingClientRect() : null;
  if (t && t.height > 0) {
    let nat = 0;
    for (const c of txt.children) nat = Math.max(nat, c.scrollWidth);
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    cw = Math.max(cw, Math.min(0.6 * vw, nat));
    ch += 1.4 * rem + t.height;
  }
  return { w: cw / 2, h: ch / 2 };
}

// Owns the one and only THREE.WebGLRenderer/Scene/Camera/composer for the
// whole page. Switching visualizers disposes the previous one and inits
// the next into a fresh THREE.Scene, but the renderer/canvas/camera/
// composer persist - recreating a WebGLRenderer on a TV-class GPU is
// exactly the kind of thing that's cheap on a desktop and janky here, so
// it just never happens.
//
// Tone mapping + bloom live here rather than per-visualizer: every
// visualizer in this pilot is additive-blended color on black, and
// without either of these that reads as flat translucent shapes
// overlapping - a 2000s-screensaver look - rather than actual light
// bleed. Both are renderer/pipeline-level concerns, so a visualizer
// never needs to know they exist; it just renders its usual colors and
// gets the same filmic treatment as every other visualizer for free.
export class SceneManager {
  constructor(canvas, options) {
    // Looked up once, measured live on every resize + poll - see
    // _measureSafeZone. This is what safeZoneUniforms' center/half-size are
    // now driven from, instead of the fixed constants safeZone.glsl.js used
    // to carry alone.
    //
    // `options.overlayEl` pour les pages dont la pochette n'est pas un
    // #overlay - la page d'accueil l'affiche dans un #track, avec le titre
    // suspendu dessous. N'importe quel objet portant getBoundingClientRect()
    // convient, ce qui permet d'en fournir un qui réunit plusieurs éléments.
    this._overlayEl = (options && options.overlayEl)
      || document.getElementById('overlay');

    this.renderer = new THREE.WebGLRenderer({
      canvas: canvas,
      // MSAA is another real per-pixel cost, and with RENDER_SCALE
      // already softening edges via upscaling (plus bloom/grain on top),
      // it's mostly redundant here - off to reclaim that budget.
      antialias: false,
      alpha: false,
      powerPreference: 'high-performance'
    });
    this.renderer.setClearColor(0x000000, 1);
    // Capped at 1 rather than 2 - a full-screen ambient visualizer
    // doesn't need retina-level density, and this is the single biggest
    // lever on any TV whose devicePixelRatio reports 2 for a 4K panel.
    // The governor's own tier (see _applyQualityTier) multiplies this cap
    // further down, not instead of it.
    this._basePixelRatioCap = Math.min(window.devicePixelRatio || 1, 1);

    // MODE ÉCONOMIE (setPowerMode). Deux leviers, tous deux sur la charge du
    // GPU, qui est ce qui chauffe une machine sur une page comme celle-ci :
    // on calcule moins de pixels, et on en calcule moins souvent. Le
    // gouverneur de tier existant ne réagit qu'une fois la machine en peine ;
    // ici on décide AVANT, parce qu'on sait qu'on est sur batterie.
    this._powerSaving = false;
    this._powerScale = 1;
    this.renderer.toneMapping = POST_DEFAULTS.toneMapping;
    this.renderer.toneMappingExposure = POST_DEFAULTS.toneMappingExposure;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    // Hardware cap on gl_PointSize. A sprite asking for more than this is
    // silently shrunk by the driver (notably on the Pi), which quietly
    // breaks any visualizer sizing point sprites in world units - so it is
    // read once here, from the only place that owns a GL context, and
    // published for whoever needs to clamp against it.
    const gl = this.renderer.getContext();
    const pointSizeRange = gl.getParameter(gl.ALIASED_POINT_SIZE_RANGE);
    maxPointSize = pointSizeRange && pointSizeRange.length > 1 ? pointSizeRange[1] : 64;
    maxAnisotropy = this.renderer.capabilities.getMaxAnisotropy();

    this.camera = new THREE.PerspectiveCamera(BASE_FOV, 1, 0.1, 100);
    this.camera.position.set(0, 1.4, 6);
    this.camera.lookAt(0, 1, 0);

    this.scene = null;
    this.activeVisualizer = null;

    this._composer = new EffectComposer(this.renderer);
    this._renderPass = new RenderPass(new THREE.Scene(), this.camera);
    this._composer.addPass(this._renderPass);
    this._bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth * BASE_RENDER_SCALE * BLOOM_SCALE, window.innerHeight * BASE_RENDER_SCALE * BLOOM_SCALE),
      0.7, // strength
      0.4, // radius
      // High threshold - PlasmaField fills the whole frame with color, and
      // a low threshold (first pass used 0.2) made bloom treat nearly the
      // entire screen as "bright", blurring the whole image into a wash
      // of white instead of glowing local highlights. Only genuinely hot
      // pixels (particle cores, beam edges, PulseCore's white center)
      // should bloom, not general midtone color.
      0.78
    );
    this._composer.addPass(this._bloomPass);
    this._filmicPass = new ShaderPass(FILMIC_SHADER);
    this._composer.addPass(this._filmicPass);
    this._composer.addPass(new OutputPass());
    // Le fondu enchaîné des titres calmes (voir crossfade-pass.js et
    // adaptive-party.js) : après OutputPass, éteint hors capture/fondu.
    this._crossfadePass = new CrossfadePass();
    this._composer.addPass(this._crossfadePass);

    // Captured fresh after every visualizer.init() (see setVisualizer) -
    // the drift below is an offset applied on top of these, never a
    // replacement for them.
    this._cameraBasePosition = new THREE.Vector3();
    this._cameraBaseQuaternion = new THREE.Quaternion();
    this._driftTime = 0;
    // What _applyCameraDrift itself last wrote to the camera - lets it
    // tell "drift moved the camera" apart from "something else moved it
    // since last frame" (see _applyCameraDrift's own re-anchor check).
    this._lastDriftedPosition = new THREE.Vector3();
    this._lastDriftedQuaternion = new THREE.Quaternion();

    this._clock = new THREE.Clock();
    this._rafId = null;
    this._profile = null;

    // Beat-grid estimation (see _updateBeatPhase) - PositionSmoother
    // extrapolates a smooth, continuously-advancing playback position
    // between Sonos polls (~3s apart - see sonos-visual-profile.js, so
    // reading profile.position directly would step in a 3s staircase
    // instead of advancing every frame) AND smooths the correction on each
    // re-anchor rather than snapping (see beat-grid.js).
    this._positionSmoother = new PositionSmoother();

    // Artwork palette blend - see beginPaletteBlend/_advancePaletteBlend.
    this._paletteBlend = null;

    // Repeated-lyric-phrase flash effect (see lyric-flash.js/lyric-hook.js)
    // - one shared instance re-attached to whatever scene is current
    // (setVisualizer), so it applies uniformly to every visualizer rather
    // than needing per-visualizer wiring.
    this._lyricLayer = new LyricFlashLayer();

    // CHANTIER 7 - performance governor state. _qualityTier/_lastFrameMs
    // feed director.js's own context every frame (see start()).
    // ?tier=<0|1|2> pins the quality tier and disarms the governor entirely -
    // no downgrades on slow frames, no upgrades on fast ones. Added for the
    // TV's temporary run-everything-at-T2 audit (its launcher passes tier=2);
    // absent or invalid, behaviour is exactly as before.
    const forcedTier = parseInt(new URLSearchParams(location.search).get('tier'), 10);
    this._forcedTier = Number.isFinite(forcedTier)
      && forcedTier >= MIN_QUALITY_TIER && forcedTier <= MAX_QUALITY_TIER
      ? forcedTier : null;
    this._qualityTier = this._forcedTier != null ? this._forcedTier : 1;
    this._lastPostProfile = undefined;
    this._lastFrameMs = 0;
    this._frameHistory = new Array(FRAME_HISTORY_SIZE).fill(1000 / 60);
    this._frameHistoryIndex = 0;
    this._frameHistorySum = this._frameHistory.reduce(function (a, b) { return a + b; }, 0);
    this._totalElapsedS = 0; // always advancing, NOT pause-gated - a paused stream can still be janky to resume into
    this._highSinceS = null;
    this._lowSinceS = null;
    this._lastTierChangeAtS = -Infinity;
    // Starts at the slowest period a real panel can have, so the very first
    // upgrade window is judged against a permissive target and then tightens
    // as the device shows what it can actually sustain.
    this._refreshMs = REFRESH_MS_MAX;
    this._locked30fps = false;
    // Cadence imposée de l'extérieur (diffusion Chromecast : la capture est à
    // 30 i/s, rendre à 60 gaspille le GPU et saccade par échantillonnage).
    this._cadence30Forcee = false;
    // Grain de film coupé (diffusion Chromecast) : re-tiré à chaque image sur
    // tout le cadre, c'est de l'entropie pure pour l'encodeur vidéo.
    this._grainCoupe = false;
    this._fixedStepAccumulatorS = 0;

    this._onResize = this._onResize.bind(this);
    this._appliedWidth = 0;
    this._appliedHeight = 0;
    window.addEventListener('resize', this._onResize);
    this._onResize();
  }

  // Raw last-rendered-frame time, in ms - the overlay (see
  // party-mode-controller.js) shows this next to its own rounded fps.
  getFrameMs() {
    return this._lastFrameMs;
  }

  getQualityTier() {
    return this._qualityTier;
  }

  // The size everything here derives from. The CANVAS's own client box, not
  // window.innerWidth/innerHeight, and that distinction is the whole fix:
  // inside the Tizen TV app the window reports a size that is not the one the
  // page is laid out at when this first runs, and no resize event ever
  // follows to correct it. The renderer therefore kept a buffer sized for the
  // wrong viewport for the entire session - blown up to the real canvas by
  // CSS, which is why the TV showed a huge, soft, washed-out picture while
  // the browser was fine. The canvas box is what is actually on screen and it
  // is right in both places.
  //
  // window.* stays as the fallback for the one frame before layout, when the
  // canvas legitimately measures 0.
  _viewportSize() {
    const c = this.renderer.domElement;
    const width = c.clientWidth || window.innerWidth;
    const height = c.clientHeight || window.innerHeight;
    return { width: width, height: height };
  }

  // Called every frame. A missed or late resize event stops being a permanent
  // wrong size and becomes at most one wrong frame - which matters because
  // the TV never fires one at all.
  _syncViewport() {
    const s = this._viewportSize();
    if (s.width === this._appliedWidth && s.height === this._appliedHeight) return;
    this._onResize();
  }

  _onResize() {
    const size = this._viewportSize();
    const width = size.width;
    const height = size.height;
    this._appliedWidth = width;
    this._appliedHeight = height;
    // Aspect ratio uses the real (unscaled) dimensions - only the actual
    // render target resolution is reduced; the canvas's CSS size (and
    // therefore what's on screen) is untouched, so this is purely an
    // internal-resolution cut, not a smaller picture.
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this._measureSafeZone(width, height);
    this._applyQualityTier();
  }

  // pixelRatio + render/bloom resolution, all tier-aware (see
  // TIER_RESOLUTION_SCALE) - called on resize AND whenever the governor
  // changes tier (see _changeQualityTier), so there is exactly one place
  // that derives these from (window size, tier) together.
  _applyQualityTier() {
    const size = this._viewportSize();
    const width = size.width;
    const height = size.height;
    const tierScale = TIER_RESOLUTION_SCALE[this._qualityTier];
    this.renderer.setPixelRatio(this._basePixelRatioCap * tierScale * this._powerScale);
    const renderWidth = Math.round(width * BASE_RENDER_SCALE);
    const renderHeight = Math.round(height * BASE_RENDER_SCALE);
    renderBufferHeight = Math.round(renderHeight * this._basePixelRatioCap * tierScale * this._powerScale);
    this.renderer.setSize(renderWidth, renderHeight, false);
    // composer.setSize() resizes every pass - including bloom - to
    // (renderWidth, renderHeight), so re-shrink bloom specifically right
    // after: its own setSize() is safe to call again immediately, and
    // this is what actually keeps bloom cheap without softening the rest
    // of the pipeline.
    this._composer.setSize(renderWidth, renderHeight);
    const bloomScale = BLOOM_SCALE * tierScale;
    this._bloomPass.setSize(renderWidth * bloomScale, renderHeight * bloomScale);
  }

  // Frame-time-driven tier change (see _recordFrameTime) - clamps, no-ops
  // if already at that tier, logs once, and re-derives pixelRatio/render/
  // bloom resolution for the new tier. Rebuilding whichever ACTIVE
  // composition is qualityTier-aware is AdaptivePartyVisualizer's own job
  // (it reads getQualityTier() itself - see its own _tickQualityTier) -
  // scene-manager has no visualizer-specific knowledge to do that here.
  _changeQualityTier(newTier) {
    if (this._forcedTier != null) return;   // pinned via ?tier= - see constructor
    const clamped = Math.max(MIN_QUALITY_TIER, Math.min(MAX_QUALITY_TIER, newTier));
    if (clamped === this._qualityTier) return;
    const previous = this._qualityTier;
    this._qualityTier = clamped;
    this._lastTierChangeAtS = this._totalElapsedS;
    // eslint-disable-next-line no-console
    console.warn('[scene-manager] quality tier changed: ' + previous + ' -> ' + clamped);
    this._applyQualityTier();
  }

  // Ring-buffer average of the last FRAME_HISTORY_SIZE frames' dt, and the
  // three governor decisions that average drives - see the constants'
  // own comments for the exact thresholds/cooldowns.
  _recordFrameTime(dtSeconds) {
    const dtMs = dtSeconds * 1000;
    this._frameHistorySum -= this._frameHistory[this._frameHistoryIndex];
    this._frameHistory[this._frameHistoryIndex] = dtMs;
    this._frameHistorySum += dtMs;
    this._frameHistoryIndex = (this._frameHistoryIndex + 1) % FRAME_HISTORY_SIZE;
    this._totalElapsedS += dtSeconds;
    const avgMs = this._frameHistorySum / FRAME_HISTORY_SIZE;

    if (avgMs > HIGH_FRAME_MS) {
      if (this._highSinceS == null) this._highSinceS = this._totalElapsedS;
      if (this._totalElapsedS - this._highSinceS >= HIGH_FRAME_SUSTAINED_S) {
        this._changeQualityTier(this._qualityTier - 1);
        this._highSinceS = null;
      }
    } else {
      this._highSinceS = null;
    }

    // Running estimate of the display's own frame period - the best sustained
    // average this device has ever actually managed. Only ever lowered, and
    // only from a full history window, so a single fast tick cannot set it.
    if (avgMs > 0 && avgMs < this._refreshMs) {
      this._refreshMs = Math.max(REFRESH_MS_MIN, avgMs);
    }

    if (avgMs < this._refreshMs * UPGRADE_SLACK) {
      if (this._lowSinceS == null) this._lowSinceS = this._totalElapsedS;
      if (this._totalElapsedS - this._lowSinceS >= LOW_FRAME_SUSTAINED_S
        && this._totalElapsedS - this._lastTierChangeAtS >= TIER_UP_COOLDOWN_S) {
        this._changeQualityTier(this._qualityTier + 1);
        this._lowSinceS = null;
      }
    } else {
      this._lowSinceS = null;
    }

    this._locked30fps = avgMs >= LOCK_30FPS_MIN_MS && avgMs <= LOCK_30FPS_MAX_MS;
  }

  // One write reaches every material: every ShaderMaterial declaring the
  // safeZone chunk shares these holder objects (see shaders/safeZone.glsl.js).
  // Called on resize AND on every poll (setProfile) - the overlay's own
  // size can change from text content alone (a longer title wrapping to a
  // second line) without a window resize ever firing.
  _measureSafeZone(width, height) {
    const w = width != null ? width : window.innerWidth;
    const h = height != null ? height : window.innerHeight;
    // THE BOX IS PARKED WHENEVER THERE IS NOTHING TO PROTECT, and there are two
    // ways that happens.
    //
    // The first is a hideOverlay visualizer: the overlay it protects is
    // invisible, so nothing on screen should be attenuated.
    //
    // The second is a PAGE WITH NO #overlay AT ALL, which was a real bug.
    // Passing null here made updateSafeZoneUniforms return after setting only
    // uAspect, leaving its fixed fallback constants in place - a box 0.44 by
    // 0.60 of the frame height, planted in the middle of the picture, on every
    // page that runs a visualizer without that element. That is most of them:
    // only party-mode.html and radio.html carry an #overlay. On the landing
    // page the phantom box was five and a half times the area of the sleeve it
    // was supposedly protecting. The fallback is there for the one frame
    // BEFORE a measurement, not for a page that will never make one.
    const parked = { left: -100000, top: -100000, width: 0, height: 0 };
    const measured = this._overlayEl ? this._overlayEl.getBoundingClientRect() : null;
    const absent = this._overlayHidden || !measured;
    // THE STAGE (R19): the centre of composition never moves; its size is the
    // cover block's AS IF CENTRED. Measured as is while the block is centred
    // (within 2 px of the screen centre), estimated from its parts while it
    // sits in a corner or in the karaoke layout. Zero when there is no cover
    // at all, like the safe box itself.
    let stageHalf = { w: 0, h: 0 };
    if (!absent) {
      const dx = measured.left + measured.width / 2 - w / 2;
      const dy = measured.top + measured.height / 2 - h / 2;
      stageHalf = Math.abs(dx) <= 2 && Math.abs(dy) <= 2
        ? { w: measured.width / 2, h: measured.height / 2 }
        : centredHalfEstimate(this._overlayEl, measured, w);
    }
    updateSafeZoneUniforms(w, h, absent ? parked : measured, stageHalf);
  }

  // Swaps in a new Visualizer instance. Disposes the current one (if
  // any) first, always into a brand new THREE.Scene so a visualizer
  // never has to worry about a previous one's leftover state.
  setVisualizer(visualizer) {
    if (this.activeVisualizer) {
      this.activeVisualizer.dispose();
    }
    this.scene = new THREE.Scene();
    this._renderPass.scene = this.scene;
    this._lyricLayer.setScene(this.scene);
    this.activeVisualizer = visualizer;
    // LA CAMÉRA EST PARTAGÉE, ET SON CHAMP DE VISION AUSSI.
    //
    // Position et orientation sont re-posées par chaque visualiseur dans son
    // init(), donc elles ne fuient pas d'un visualiseur au suivant. Le champ
    // de vision, lui, n'était écrit qu'une fois à la construction - si bien
    // qu'un visualiseur qui le modifiait le laissait modifié POUR TOUTE LA
    // VIE DE LA PAGE. C'était le cas d'ocean.js, qui le resserre à 55° :
    // après son passage, les 56 autres tournaient un tiers trop serré, et sur
    // main-stage les tours de structure sortaient du cadre.
    //
    // On repart donc de la référence avant chaque init(). Un visualiseur qui
    // veut un autre cadrage le pose lui-même, comme ocean le fait déjà.
    reinitialiserCamera(this.camera);
    visualizer.init(this.scene, this.camera);
    // Forces _syncPostProfile below to apply on the next frame whatever the
    // incoming visualizer wants, including "nothing", which restores the
    // defaults the outgoing one may have overridden.
    this._lastPostProfile = undefined;
    // The visualizer just set its intended static position/lookAt -
    // that's the center the drift below oscillates around, not 0/identity.
    this._cameraBasePosition.copy(this.camera.position);
    this._cameraBaseQuaternion.copy(this.camera.quaternion);
    this._lastDriftedPosition.copy(this.camera.position);
    this._lastDriftedQuaternion.copy(this.camera.quaternion);
    if (this._profile) {
      visualizer.onTrackChange(this._profile);
      visualizer.onPlayPause(this._profile.playbackState);
    }
  }

  // Checked every frame rather than only on setVisualizer, because on the
  // orchestrated path setVisualizer only ever sees AdaptivePartyVisualizer -
  // the composition actually on screen is swapped inside it, long afterwards
  // and without this class being told. AdaptiveParty forwards its active
  // composition's profile through its own `postProcessing` getter, so reading
  // it here per frame is what makes an override work in the rotation and not
  // only from the debug switcher. An identity compare, so the usual case (a
  // visualizer with no overrides, frame after frame) costs one comparison.
  _syncPostProfile() {
    const profile = this.activeVisualizer ? this.activeVisualizer.postProcessing : null;
    if (profile !== this._lastPostProfile) {
      this._lastPostProfile = profile;
      this._applyPostProfile(profile);
    }
    // Same per-frame contract for `hideOverlay` (Silk is the first: its veil
    // already carries the cover, so the DOM overlay both repeats it and sits
    // in front of the visual). The class fades the overlay via CSS; the safe
    // zone is neutralised alongside (see _measureSafeZone) because dimming
    // the scene behind an overlay nobody can see would just paint a dark
    // rectangle on the cloth.
    const hide = !!(this.activeVisualizer && this.activeVisualizer.hideOverlay);
    if (hide !== this._overlayHidden) {
      this._overlayHidden = hide;
      if (typeof document !== 'undefined' && document.body) {
        document.body.classList.toggle('overlay-hidden', hide);
      }
      this._measureSafeZone();
    }

    // `hideOverlayArtwork` is the narrower version of the same contract:
    // hide the COVER IMAGE only, keeping the title and artist. Shatter is
    // the first to need it - it renders the cover itself, as the pane of
    // glass that breaks, so while the DOM cover sits on top the artwork
    // visibly never shatters (it is a static image in front of the
    // fragments). The safe zone is deliberately NOT neutralised here: the
    // text is still there to protect.
    const hideArt = !!(this.activeVisualizer && this.activeVisualizer.hideOverlayArtwork);
    if (hideArt !== this._overlayArtHidden) {
      this._overlayArtHidden = hideArt;
      if (typeof document !== 'undefined' && document.body) {
        document.body.classList.toggle('overlay-art-hidden', hideArt);
      }
    }
  }

  // Merges a visualizer's own `postProcessing` over POST_DEFAULTS and writes
  // the result to the passes. The previous visualizer's overrides are always
  // undone, even when the incoming one has none of its own.
  _applyPostProfile(overrides) {
    const p = Object.assign({}, POST_DEFAULTS, overrides || {});
    this._filmicPass.uniforms.uAberration.value = p.aberration;
    this._filmicPass.uniforms.uVignette.value = p.vignette;
    this._filmicPass.uniforms.uGrain.value = this._grainCoupe ? 0 : p.grain;
    this._bloomPass.strength = p.bloomStrength;
    this._bloomPass.threshold = p.bloomThreshold;
    // Changing toneMapping invalidates every compiled material, which is why
    // this only ever happens on a swap - when the scene is being rebuilt from
    // nothing anyway - and never per frame.
    if (this.renderer.toneMapping !== p.toneMapping) {
      this.renderer.toneMapping = p.toneMapping;
      this.scene.traverse(function (obj) {
        if (obj.material) {
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
          for (const m of mats) m.needsUpdate = true;
        }
      });
    }
    this.renderer.toneMappingExposure = p.toneMappingExposure;
  }

  // Feeds the latest SonosVisualProfile in; also detects track-change/
  // play-pause edges so visualizers get onTrackChange/onPlayPause exactly
  // once per transition rather than having to diff it themselves every
  // frame.
  // Le réglage de luminosité de la console : un facteur sur l'animation seule
  // (voir uLuminosite dans FILMIC_SHADER). Borné pour qu'une valeur folle ne
  // puisse ni éteindre l'écran ni le saturer.
  setLuminosite(facteur) {
    const f = Number(facteur);
    this._filmicPass.uniforms.uLuminosite.value = Number.isFinite(f) ? Math.max(0.2, Math.min(3, f)) : 1;
  }

  setProfile(profile) {
    const previous = this._profile;
    this._profile = profile;
    // Re-anchored on every poll (not just track changes) so drift never
    // accumulates past a few seconds' worth of estimation error, and so a
    // seek/skip is picked up within one poll interval rather than
    // compounding. PositionSmoother itself decides whether this re-anchor
    // needs a smoothed 400ms correction or can be accepted outright - see
    // beat-grid.js.
    const bpm = profile.trackFeatures && profile.trackFeatures.bpm;
    this._positionSmoother.reanchor(profile.position, bpm, performance.now() / 1000);
    this._measureSafeZone();
    if (!this.activeVisualizer) return;
    if (!previous || previous.trackId !== profile.trackId) {
      this.activeVisualizer.onTrackChange(profile);
    }
    if (!previous || previous.playbackState !== profile.playbackState) {
      this.activeVisualizer.onPlayPause(profile.playbackState);
    }
  }

  // Offsets the camera around whatever base position/lookAt the active
  // visualizer set in init() - see the DRIFT_* constants and
  // setVisualizer's capture of _cameraBasePosition/_cameraBaseQuaternion.
  // Frozen on pause, same "freeze don't extinguish" rule as everything
  // else driven by playbackState in this pilot.
  _applyCameraDrift(dt) {
    // Re-anchor if something other than this method's own last write
    // moved the camera since then - e.g. AdaptivePartyVisualizer swapping
    // to a new inner composition, whose own init() just repositioned the
    // camera for itself (setVisualizer, which normally captures the
    // drift base, only runs once per TOP-LEVEL visualizer switch, not on
    // every inner swap AdaptivePartyVisualizer makes on its own). Without
    // this, that new position/orientation got silently overwritten on the
    // very next frame by drift still oscillating around whatever base was
    // captured back when AdaptiveParty first became active - DaftPyramid
    // (and any other inner composition that sets its own camera framing)
    // "arriving each time with different vertical positioning" was this:
    // its own careful camera.position.set() in init() discarded in favor
    // of a stale base left over from whichever composition AdaptiveParty
    // happened to build first that session.
    if (!this.camera.position.equals(this._lastDriftedPosition) || !this.camera.quaternion.equals(this._lastDriftedQuaternion)) {
      this._cameraBasePosition.copy(this.camera.position);
      this._cameraBaseQuaternion.copy(this.camera.quaternion);
    }

    const paused = this._profile && this._profile.playbackState !== 'PLAYING';
    if (!paused) this._driftTime += dt;
    const t = this._driftTime;

    this.camera.position.set(
      this._cameraBasePosition.x + Math.sin(t * 0.11) * DRIFT_POSITION_AMOUNT,
      this._cameraBasePosition.y + Math.sin(t * 0.07 + 1.7) * DRIFT_POSITION_AMOUNT * 0.6,
      this._cameraBasePosition.z + Math.sin(t * 0.05 + 3.1) * DRIFT_POSITION_AMOUNT * 0.4
    );

    _driftEuler.set(
      Math.sin(t * 0.06 + 0.5) * DRIFT_ROTATION_AMOUNT,
      Math.sin(t * 0.08) * DRIFT_ROTATION_AMOUNT,
      0
    );
    _driftQuat.setFromEuler(_driftEuler);
    this.camera.quaternion.copy(this._cameraBaseQuaternion).multiply(_driftQuat);

    this._lastDriftedPosition.copy(this.camera.position);
    this._lastDriftedQuaternion.copy(this.camera.quaternion);
  }

  // Writes the full musical grid (see beat-grid.js's computeBeatGrid) onto
  // the shared profile object: beatPhase/beatIndex as before, plus
  // barPhase/barIndex, phrasePhase/phraseIndex (16 beats),
  // sectionPhase/sectionIndex (32 beats), accent and beatConfidence - all
  // null whenever there's no BPM to work with (see
  // trackFeaturesService.js's fallback) or while not actively playing, so a
  // visualizer reading them can simply treat null as "no beat data
  // available" rather than a special case. Mutates profile in place - every
  // visualizer's update() already receives this exact object each frame.
  _updateBeatPhase() {
    if (!this._profile) return;
    const bpm = this._profile.trackFeatures && this._profile.trackFeatures.bpm;
    const now = performance.now() / 1000;
    // Smoothly-extrapolated position, written back onto the profile every
    // frame - unlike profile.position (the raw Sonos value, frozen
    // between ~3s polls), this is what anything needing frame-accurate
    // timing should read: adaptive-party.js's forced-swap detection
    // (radiopi segment/drop marks) and its energy lookup both switched to
    // this after being found to only re-check every 3s via profile.position,
    // a real desync source. Independent of bpm (PositionSmoother.estimate()
    // needs none - bpm only tunes reanchor()'s own correction threshold),
    // so it's available even for a track with no known tempo.
    this._profile.estimatedPosition = this._profile.playbackState === 'PLAYING' ? this._estimatePosition() : this._profile.position;
    const position = bpm && this._profile.playbackState === 'PLAYING' ? this._profile.estimatedPosition : null;
    if (position == null) {
      Object.assign(this._profile, {
        beatPhase: null, beatIndex: null,
        barPhase: null, barIndex: null,
        phrasePhase: null, phraseIndex: null,
        sectionPhase: null, sectionIndex: null,
        accent: null, beatConfidence: null
      });
      return;
    }
    // Real analysis timeline (radiopi /analyze - see track-analysis.js's
    // buildAnalysisTimeline) takes over the whole beat grid when it
    // exists: a genuine measured beat_times_ms lookup, not a constant-
    // tempo extrapolation, so it never drifts between Sonos polls the way
    // PositionSmoother's own re-anchoring exists to paper over. beatPhase
    // stays exact even mid-poll-interval. beatConfidence still comes from
    // the position smoother either way - that's about how accurately
    // POSITION itself is tracked, not about the beat math built on top of
    // it, which is exact given a correct position when an analysis
    // timeline is available.
    const timeline = this._profile.analysisTimeline;
    const grid = timeline ? timeline.lookupBeatPhase(position * 1000) : computeBeatGrid(bpm, position);
    Object.assign(this._profile, grid, { beatConfidence: this._positionSmoother.confidence(now) });
  }

  // Cross-fades the palette the VISUALIZERS see, from whatever they were
  // showing to the new cover's, over a window slightly longer than the DOM
  // crossfade - so the scene finishes turning just after the picture does,
  // which reads as a consequence of it rather than as a separate event.
  //
  // STEPPED, not per-frame, and that is not a shortcut. Fourteen of the
  // visualizers guard their colour work with
  //   if (profile.artworkPalette !== this._lastPaletteRef)
  // an IDENTITY check - and several of them answer it by looping over every
  // particle and re-uploading a colour attribute (disco-squares,
  // spot-wall, neon-particles). Publishing a freshly
  // interpolated object every frame would turn that guard into a per-frame
  // attribute upload across the whole library, which the performance contract
  // forbids outright. A handful of intermediate objects gives the eye a
  // transition and costs those visualizers PALETTE_BLEND_STEPS rebuilds spread
  // over the window instead of one - and this is precisely the moment the
  // brief warns about, with an image decode already in flight.
  // Returns true if it took ownership of publishing the palette, false if the
  // caller must publish it itself (no previous palette to blend from).
  //
  // MUST be called BEFORE the target is written onto the profile: `from` is
  // read off the profile here, so publishing first makes from === target and
  // the blend a silent no-op that still costs every visualizer its rebuilds.
  beginPaletteBlend(target) {
    if (!target || !this._profile) return false;
    const from = this._profile.artworkPalette;
    // Nothing to blend FROM (first track of the session, or no previous
    // cover): a blend from nothing is just a slower snap, so let the caller
    // publish directly.
    if (!from) { this._paletteBlend = null; return false; }
    this._paletteBlend = {
      from: from,
      target: target,
      startedAt: performance.now(),
      stepsDone: 0
    };
    return true;
  }

  _advancePaletteBlend() {
    const blend = this._paletteBlend;
    if (!blend || !this._profile) return;
    const t = (performance.now() - blend.startedAt) / PALETTE_BLEND_MS;
    if (t >= 1) {
      // Land on the real target object, not on a 99%-of-the-way copy - so the
      // palette every visualizer ends up holding is byte-for-byte the one
      // extraction produced.
      this._profile.artworkPalette = blend.target;
      this._paletteBlend = null;
      return;
    }
    const step = Math.floor(t * PALETTE_BLEND_STEPS);
    if (step <= blend.stepsDone) return;
    blend.stepsDone = step;
    this._profile.artworkPalette = lerpPalette(blend.from, blend.target, step / PALETTE_BLEND_STEPS);
  }

  // Smoothly-extrapolated playback position (seconds) between Sonos'
  // own ~3s polls - shared by _updateBeatPhase and the lyric-flash
  // layer's own line-timing lookup, so both read the exact same clock.
  _estimatePosition() {
    return this._positionSmoother.estimate(performance.now() / 1000);
  }

  // The actual per-frame work (director, visualizer update, drift, lyric
  // layer, composer render) - split out of start()'s own RAF loop so the
  // fixed-30fps lock (see _recordFrameTime/CHANTIER 7) can skip calling it
  // on frames it isn't stepping.
  _renderFrame(dt) {
    // Before anything is drawn - see _syncViewport.
    this._syncViewport();
    this._advancePaletteBlend();
    this._updateBeatPhase();
    if (this._profile) {
      // Computed once per frame, attached in place - see director.js.
      // AdaptivePartyVisualizer (the real top-level visualizer) may
      // adjust profile.director.intensity/flash during its own update()
      // below, before delegating to whichever inner composition is
      // active - see its own armSwap()/pre-roll (chantier 6).
      this._profile.director = computeDirector(this._profile, {
        qualityTier: this._qualityTier,
        frameMs: this._lastFrameMs,
        driftTime: this._driftTime
      });
    }
    if (this.activeVisualizer && this._profile) {
      this.activeVisualizer.update(dt, this._profile);
    }
    // After update(), so a composition swapped this frame is already the one
    // being asked - see _syncPostProfile.
    this._syncPostProfile();
    this._applyCameraDrift(dt);
    this._filmicPass.uniforms.uFlash.value = this._profile && this._profile.director ? this._profile.director.flash : 0;
    // Same one-frame-wide contract as flash: fadeCapture asks for THIS
    // frame (the outgoing composition's last) to be kept; fadeMix/fadeDip
    // are set every frame of a fade by adaptive-party.js. The director is
    // recomputed each frame, so a visualizer that does not set them leaves
    // the pass off.
    const dir = this._profile && this._profile.director;
    const fadeMix = dir && dir.fadeMix > 0 ? dir.fadeMix : 0;
    const fadeCapture = !!(dir && dir.fadeCapture);
    this._crossfadePass.capture = fadeCapture;
    this._crossfadePass.mix = fadeMix;
    this._crossfadePass.dip = dir && dir.fadeDip ? dir.fadeDip : 0;
    this._crossfadePass.enabled = fadeCapture || fadeMix > 0.001;
    this._lastFrameMs = dt * 1000;
    const playing = !!this._profile && this._profile.playbackState === 'PLAYING';
    // profile.lyricHooks is read fresh every frame here (not cached
    // via some earlier setProfile-time snapshot) because main.js's
    // async lyrics lookup resolves by mutating the SAME profile object
    // in place, well after setProfile last ran for it - reading it
    // live each frame is what actually catches that update promptly,
    // the same way every visualizer already reads profile.trackFeatures/
    // artworkPalette fresh each frame instead of being handed a
    // one-time snapshot.
    this._lyricLayer.update(this.camera, this._profile ? this._profile.lyricHooks : null, this._estimatePosition(), playing, this._profile ? this._profile.beatPhase : null);
    this._filmicPass.uniforms.uTime.value += dt;
    this._composer.render();
  }

  // ?freezetime=<s> (debug-capture.js): no requestAnimationFrame loop. The
  // virtual clock is stepped by exactly 1/60 s per frame from the moment the
  // visualizer was posed, up to <s> s, then the last frame stays on screen.
  // The real-time pause before it lets textures and workers arrive.
  renderFrozen(seconds) {
    const n = Math.round((seconds != null ? seconds : FREEZE_TIME_S || 0) / FREEZE_STEP_S);
    for (let k = 0; k < Math.max(1, n); k++) {
      advanceFrozenClock(FREEZE_STEP_S * 1000);
      this._renderFrame(FREEZE_STEP_S);
    }
  }

  start() {
    if (this._rafId !== null) return;
    if (FREEZE_TIME_S != null) {
      if (!this._freezeTimer) this._freezeTimer = setTimeout(() => { this._freezeTimer = null; this.renderFrozen(); }, 2500);
      return;
    }
    const loop = () => {
      this._rafId = requestAnimationFrame(loop);
      // Le navigateur ne sert AUCUNE image à une page cachée : au retour, la
      // première mesure vaut toute la durée de l'absence - trente secondes,
      // cinq minutes. Sans plafond, la scène avance de tout ça d'un coup, ce
      // qui se voit comme un saut, et le régulateur de cadence prend cette
      // absence pour une image très lente et se verrouille à tort.
      const dt = Math.min(this._clock.getDelta(), MAX_FRAME_DT_S);
      // Frame-time governor sees EVERY raw tick, locked or not - it needs
      // the real cadence to decide whether to unlock again later.
      this._recordFrameTime(dt);

      // Le pas fixe sert aussi bien au gouverneur qu'au mode économie : un
      // trente images par seconde régulier vaut mieux qu'un soixante qui
      // vide la batterie, et l'image ne s'en ressent presque pas sur des
      // visuels de cette nature.
      if (!this._locked30fps && !this._powerSaving && !this._cadence30Forcee) {
        this._fixedStepAccumulatorS = 0;
        this._renderFrame(dt);
        return;
      }

      // 17-30ms average -> steady 30fps beats an oscillating 40-50fps (see
      // LOCK_30FPS_MIN/MAX's own comment). Accumulate real time and only
      // step/render once a full 1/30s has built up - clamped so a single
      // long stall (tab backgrounded, GC pause) doesn't demand a big
      // catch-up jump once it returns.
      this._fixedStepAccumulatorS += dt;
      if (this._fixedStepAccumulatorS < FIXED_TIMESTEP_30FPS_S) return;
      const stepDt = Math.min(this._fixedStepAccumulatorS, FIXED_TIMESTEP_30FPS_S * 2);
      this._fixedStepAccumulatorS -= FIXED_TIMESTEP_30FPS_S;
      if (this._fixedStepAccumulatorS < 0) this._fixedStepAccumulatorS = 0;
      this._renderFrame(stepDt);
    };
    this._rafId = requestAnimationFrame(loop);
  }

  // Économie d'énergie : trois quarts de résolution et trente images par
  // seconde. Se pose et se retire à chaud - on branche et débranche un
  // ordinateur portable en pleine écoute.
  // FIXER LA CADENCE À 30 i/s, au pas fixe, sans toucher à la définition —
  // pour un écran dont la capture vidéo est elle-même à 30 (Chromecast en
  // mode miroir). Le gouverneur continue de mesurer la cadence brute.
  couperGrain(oui) {
    this._grainCoupe = !!oui;
    if (this._grainCoupe && this._filmicPass) this._filmicPass.uniforms.uGrain.value = 0;
  }

  fixerCadence30(oui) {
    this._cadence30Forcee = !!oui;
    if (!this._cadence30Forcee) this._fixedStepAccumulatorS = 0;
  }

  setPowerMode(saving) {
    const on = !!saving;
    if (on === this._powerSaving) return;
    this._powerSaving = on;
    this._powerScale = on ? POWER_SAVE_SCALE : 1;
    if (this.renderer) this._applyQualityTier();
  }

  get powerSaving() { return this._powerSaving; }

  stop() {
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }
}
